import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import { ConditionItem } from './ConditionItem';

import { loadClaimConditions } from '../../../../actions';
import { generateUUID } from '../../../../services/utils';

import style from './ConditionsSelector.module.css';

// for handling dynamic condition inputs
const CONDITION_SELECTION = {
  Date: '', // condition id required by submit claim
  Id: 0, // condition id required by submit claim
  internalId: generateUUID(), // for identifying dynimic components created
  label: null, // react-select required
  value: null, // react-select required
};

const ConditionsSelector = ({
  conditions = [],
  conditionsCatalog = [],
  loading = false,
  marketingChannelId = 0,
  maxDate = '',
  minDate = '',
  onConditionRemoved = () => {},
  onConditionsSelected = () => {},
}) => {
  const { t } = useTranslation('fileClaimbot');
  const [conditionInputs, setConditionInputs] = useState([CONDITION_SELECTION]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (conditions.length) {
      // initialize condition inputs with existing conditions
      setConditionInputs(conditions);
    }
  }, []);

  useEffect(() => {
    // update the parent component with the selected conditions
    onConditionsSelected(conditionInputs);
  }, [conditionInputs]);

  useEffect(() => {
    // listen to language selection and marketing channel changes
    dispatch(loadClaimConditions({ marketingChannelId }));
  }, [i18next.language, marketingChannelId]);

  const getConditionLabelById = useCallback((conditionId) => {
    if (conditionsCatalog && conditionsCatalog.length > 0) {
      const conditionFound = conditionsCatalog
        .find((condition) => condition.value === conditionId);

      return conditionFound?.label;
    }

    return '';
  }, [conditionsCatalog]);

  useEffect(() => {
    // translates selected conditions if catalag and user selection
    // are available only
    if (conditionsCatalog.length > 0 && conditionInputs.length > 0) {
      setConditionInputs((prev) => prev.map((condition) => ({
        ...condition,
        label: getConditionLabelById(condition.Id) || condition.label,
      })));
    }
  }, [conditionsCatalog]);

  const onAddAnotherCondition = () => {
    setConditionInputs([
      ...conditionInputs,
      {
        ...CONDITION_SELECTION,
        internalId: generateUUID(),
      },
    ]);
  };

  const onRemoveACondition = (conditionId) => () => {
    // remove condition notification
    onConditionRemoved(conditionId);
    setConditionInputs((prev) => prev
      .filter((condition) => condition.internalId !== conditionId));
  };

  const onConditionTypeSelected = (conditionId) => (newCondition) => {
    setConditionInputs((prev) => prev.map((condition) => {
      if (condition.internalId === conditionId) {
        return {
          ...condition,
          Id: newCondition.value,
          label: newCondition.label,
          value: newCondition.value,
        };
      }
      return condition;
    }));
  };

  const onConditionDateSelected = (conditionId) => (date) => {
    setConditionInputs((prev) => prev.map((condition) => {
      if (condition.internalId === conditionId) {
        return {
          ...condition,
          Date: date,
        };
      }
      return condition;
    }));
  };

  return (
    <section className={style.conditionsListContainer}>
      {conditionInputs.map((condition) => (
        <ConditionItem
          key={condition.internalId}
          conditionDate={condition.Date}
          conditionsCatalog={conditionsCatalog}
          conditionSelected={condition} // selected by label-value format
          loading={loading}
          maxDate={maxDate}
          minDate={minDate}
          onConditionDateSelected={onConditionDateSelected(
            condition.internalId,
          )}
          onConditionTypeSelected={onConditionTypeSelected(
            condition.internalId,
          )}
          onRemoveCondition={onRemoveACondition(
            condition.internalId,
          )}
          removable={conditionInputs.length > 1}
        />
      ))}

      <button
        className={style.addAnotherConditionButton}
        onClick={onAddAnotherCondition}
        type="button"
      >
        {t('conditions.add')}
      </button>
    </section>
  );
};

export { ConditionsSelector };
